import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCw } from 'lucide-react';
import { Refresh } from 'iconoir-react';

import { useTheme } from 'components/core/theme-provider';
import { Button } from 'components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { SparkleText } from 'features/chat/agent-chat-module/utils/chat-event-messages';
import { useAgentPreviewChat } from 'features/chat/hooks/use-agent-preview-chat';
import { StreamActivityDropdown } from 'features/chat/components/StreamActivityDropdown';
import { McpServiceAuthModal } from 'features/chat/components/McpServiceAuthModal';
import { useMyPlan } from 'features/subscription/hooks/use-seats';
import { cn } from 'lib/utils';
import { getPickerTheme } from 'pages/agents/utils/agent-config-picker-theme';
import type { ChatbotWidget } from './index.type';
import { DateSeparator, PreviewChatItem } from './preview-chat-item';
import { PreviewChatInput } from './preview-chat-input';
import { PreviewChatItemSuggestions } from './preview-chat-item-suggestions';

type PreviewChatbotProps = {
  agentId: string;
  widgetId: string;
  projectKey: string;
  widget: ChatbotWidget;
  humanLogoUrl?: string;
  lastUpdatedDate?: string;
  isSaving?: boolean;
  lastSaveTime?: Date | null;
  onResetChat?: () => void;
  /** When true, header/chat/input use widget designSettings colors; otherwise theme defaults. */
  applyWidgetColors?: boolean;
  /** Edit-agent preview: black header, light/dark chat body. */
  variant?: 'default' | 'edit';
  className?: string;
};

export type { PreviewChatbotProps };

export function PreviewChatbot({
  agentId,
  widgetId,
  projectKey,
  widget,
  onResetChat,
  applyWidgetColors = false,
  variant = 'default',
  className,
}: PreviewChatbotProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const ui = getPickerTheme(theme === 'dark');
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    conversations,
    conversationId,
    suggestions,
    streamStatusEntries,
    isBotThinking,
    isBotStreaming,
    isResponseGenerating,
    isConnected,
    sendMessage,
  } = useAgentPreviewChat({ agentId, widgetId });
  const { hasPlan, isLoading: isPlanLoading } = useMyPlan();
  const isChatDisabled = !hasPlan || !isConnected || !agentId || isResponseGenerating;

  const hasStreamStatus = (streamStatusEntries?.length ?? 0) > 0;
  // Live Steps only before bot text exists; once content arrives, Steps live in the bubble.
  const showStreamStatus = isBotThinking && hasStreamStatus;
  const showSending = isBotThinking && !hasStreamStatus;
  const lastStreamIcon = streamStatusEntries?.length
    ? [...streamStatusEntries].reverse().find((entry) => !entry.todos?.length)?.icon ??
      streamStatusEntries[streamStatusEntries.length - 1]?.icon
    : undefined;
  const streamStepsInProgress =
    showStreamStatus &&
    (lastStreamIcon === 'tool_started' || lastStreamIcon === 'subagent_started');

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.scrollTo?.({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [conversations, suggestions, isBotThinking, isBotStreaming, streamStatusEntries]);

  const shouldShowDateSeparator = (currentTimestamp?: string, previousTimestamp?: string) => {
    if (!currentTimestamp) return false;
    if (!previousTimestamp) return true;

    return new Date(currentTimestamp).toDateString() !== new Date(previousTimestamp).toDateString();
  };

  const chatHeaderStyle = useMemo(() => {
    if (!applyWidgetColors) {
      return undefined;
    }

    const ds = widget.designSettings;
    const start = ds.headerStartColor?.trim() || '#000000';
    const end = ds.headerEndColor?.trim() || '#000000';
    const titleColor = ds.titleColor?.trim() || '#FFFFFF';

    return {
      backgroundImage: `linear-gradient(90deg, ${start}, ${end})`,
      color: titleColor,
    };
  }, [applyWidgetColors, widget.designSettings]);

  const chatBodyStyle = useMemo(() => {
    if (!applyWidgetColors) {
      return undefined;
    }

    return {
      backgroundColor: widget.designSettings.chatBackgroundColor || undefined,
    };
  }, [applyWidgetColors, widget.designSettings]);

  const chatFontColor = applyWidgetColors
    ? widget.designSettings.chatFontColor?.trim() || '#000000'
    : undefined;

  const isEditPreview = variant === 'edit';
  const displayName = widget.name || t('SYSTEM', { defaultValue: 'System' });
  const nameInitial = displayName.trim().charAt(0).toUpperCase() || 'A';

  // Wait for the plan check so the field doesn't flash locked for people who have a plan.
  const lockedMessage =
    !isPlanLoading && !hasPlan ? t('NO_PLAN_GET_TOP_UP', 'No plan. Get a plan or top up to chat.') : undefined;

  const messageArea = (
    <>
      <div className={isEditPreview ? 'flex w-full items-start px-4.5 pt-4' : undefined}>
        <PreviewChatItem
          message={widget.greeting}
          isMarkedDown
          showAgentName={false}
          fontColor={chatFontColor}
        />
      </div>

      {conversations.map((item, index) => {
        const previousItem = conversations[index - 1];
        const showDateSeparator = shouldShowDateSeparator(item.timestamp, previousItem?.timestamp);
        const messageColor = chatFontColor;
        const isLatestBot =
          item.type === 'bot' &&
          !conversations.slice(index + 1).some((next) => next.type === 'bot');

        return (
          <div
            key={item.id}
            className={cn('min-w-0 w-full max-w-full', isEditPreview && 'px-4.5')}
          >
            {showDateSeparator && item.timestamp ? (
              <DateSeparator date={item.timestamp} fontColor={messageColor} />
            ) : null}
            <PreviewChatItem
              name={item.type === 'bot' ? widget.name || t('ASSISTANT') : t('YOU')}
              message={item.message}
              isMarkedDown={item.type === 'bot'}
              isBot={item.type === 'bot'}
              timestamp={item.timestamp}
              showAgentName={false}
              fontColor={messageColor}
              a2uiSurfaces={item.a2uiSurfaces}
              streamActivityEntries={item.streamActivityEntries}
              streamBlocks={item.streamBlocks}
              isStreaming={Boolean(item.streaming)}
              conversationId={conversationId}
              userMessageId={item.userRecordId}
              assistantMessageId={item.messageId}
              isLatest={isLatestBot}
            />
          </div>
        );
      })}

      {(showSending || showStreamStatus) && (
        <div
          className={cn(
            'duration-500 animate-in fade-in slide-in-from-left-2',
            isEditPreview && 'px-4.5',
          )}
        >
          {showSending ? (
            <SparkleText text={t('SENDING', { defaultValue: 'Sending...' })} />
          ) : null}
          {showStreamStatus ? (
            <StreamActivityDropdown
              entries={streamStatusEntries ?? []}
              active={showStreamStatus}
              defaultOpen
              forceOpen={streamStepsInProgress}
              shimmerActiveRow={streamStepsInProgress}
            />
          ) : null}
        </div>
      )}

      {hasPlan && suggestions.length > 0 ? (
        <div className={isEditPreview ? 'px-4.5' : undefined}>
          <PreviewChatItemSuggestions suggestions={suggestions} onSelect={sendMessage} />
        </div>
      ) : null}
    </>
  );

  return (
    <div
      id={`chatbot-container-${widgetId}`}
      className={cn(
        'flex h-full w-full flex-col items-start',
        isEditPreview && cn('overflow-hidden rounded-2xl border', ui.chipWrap),
        className,
      )}
    >
      <div
        data-testid="preview-chatbot-header"
        className={
          isEditPreview
            ? cn(
                'flex w-full shrink-0 items-center gap-3 px-4 py-3',
                applyWidgetColors
                  ? 'text-true-white'
                  : 'bg-black text-true-white dark:bg-popover'
              )
            : applyWidgetColors
              ? 'sticky top-0 z-10 flex w-full shrink-0 items-center justify-between px-4 py-3'
              : 'sticky top-0 z-10 flex w-full shrink-0 items-center justify-between bg-primary px-4 py-3 text-primary-foreground'
        }
        style={chatHeaderStyle}
      >
        {isEditPreview ? (
          <div
            className="flex size-8 shrink-0 items-center justify-center rounded-full border border-white bg-white/15 text-xs font-semibold text-true-white"
            data-testid="preview-chatbot-avatar"
            aria-hidden
          >
            {nameInitial}
          </div>
        ) : null}
        <h4
          className={
            isEditPreview
              ? 'min-w-0 flex-1 truncate text-sm font-semibold text-true-white'
              : 'min-w-0 flex-1 truncate pr-2 text-sm font-semibold'
          }
          style={chatHeaderStyle ? { color: chatHeaderStyle.color } : undefined}
          title={displayName}
        >
          {displayName}
        </h4>
        {onResetChat ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={onResetChat}
                  className={
                    isEditPreview
                      ? 'size-8 rounded-full text-true-white hover:bg-white/10 hover:text-true-white'
                      : 'h-8 w-8 rounded-full text-inherit hover:bg-black/10'
                  }
                  style={chatHeaderStyle ? { color: chatHeaderStyle.color } : undefined}
                >
                  {isEditPreview ? (
                    <Refresh width={16} height={16} strokeWidth={1.5} />
                  ) : (
                    <RotateCw className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('REFRESH')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>

      {isEditPreview ? (
        <div
          className="flex min-h-0 w-full flex-1 flex-col justify-between px-3 pb-3 pt-2"
          style={chatBodyStyle}
        >
          <div
            ref={containerRef}
            className={cn(
              'flex min-h-0 w-full flex-1 flex-col overflow-x-hidden overflow-y-auto text-sm font-medium leading-body',
              !applyWidgetColors && ui.body,
            )}
            style={chatBodyStyle}
          >
            {messageArea}
          </div>

          <PreviewChatInput
            config={widget.designSettings}
            applyWidgetColors={applyWidgetColors}
            variant={variant}
            onSubmit={sendMessage}
            disable={isChatDisabled}
            lockedMessage={lockedMessage}
          />
        </div>
      ) : (
        <>
          <div
            ref={containerRef}
            className={
              applyWidgetColors
                ? 'flex min-h-0 w-full flex-1 flex-col gap-6 overflow-x-hidden overflow-y-auto border-x px-4 py-3 text-base font-normal'
                : 'flex min-h-0 w-full flex-1 flex-col gap-6 overflow-x-hidden overflow-y-auto border-x bg-background px-4 py-3 text-base font-normal text-foreground'
            }
            style={chatBodyStyle}
          >
            {messageArea}
          </div>

          <div className="w-full shrink-0">
            <PreviewChatInput
              config={widget.designSettings}
              applyWidgetColors={applyWidgetColors}
              variant={variant}
              onSubmit={sendMessage}
              disable={isChatDisabled}
              lockedMessage={lockedMessage}
            />
          </div>
        </>
      )}
      <McpServiceAuthModal />
    </div>
  );
}
